import React, { useState, useEffect } from 'react';
import {
  TextField,
  NumberField,
  BooleanField,
  SelectField,
  DateField,
  ReferenceField,
  ReferenceArrayField,
  StringArrayField
} from './FormFields';
import { DiscriminatorField } from './DiscriminatorField';
import { ObjectField } from './ObjectField';
import { ArrayOfObjectsField } from './ArrayOfObjectsField';

// Main function to render the appropriate field based on type
export const renderField = (fieldName, fieldDef, value, onChange, formData, depth = 0) => {
  const error = null; // You can add validation errors here

  // Skip read-only fields in create mode
  if (fieldDef.readOnly && !value) {
    return null;
  }

  switch (fieldDef.type) {
    case 'String':
      if (fieldDef.enum) {
        return <SelectField field={fieldDef} value={value} onChange={onChange} error={error} />;
      }
      return <TextField field={fieldDef} value={value} onChange={onChange} error={error} />;

    case 'Number':
      return <NumberField field={fieldDef} value={value} onChange={onChange} error={error} />;

    case 'Boolean':
      return <BooleanField field={fieldDef} value={value} onChange={onChange} error={error} />;

    case 'Date':
      return <DateField field={fieldDef} value={value} onChange={onChange} error={error} />;

    case 'ObjectId':
      return <ReferenceField field={fieldDef} value={value} onChange={onChange} error={error} />;

    case 'Array':
      if (fieldDef.arrayType === 'ObjectId') {
        return <ReferenceArrayField field={fieldDef} value={value} onChange={onChange} error={error} />;
      } else if (fieldDef.arrayType === 'String' && fieldDef.options) {
        return <StringArrayField field={fieldDef} value={value} onChange={onChange} error={error} />;
      }
      return <TextField field={fieldDef} value={value} onChange={onChange} error={error} />;

    case 'ArrayOfObjects':
      return <ArrayOfObjectsField field={fieldDef} value={value} onChange={onChange} error={error} />;

    case 'Discriminator':
      return <DiscriminatorField field={fieldDef} value={value} onChange={onChange} error={error} parentFormData={formData} />;

    case 'Object':
      return <ObjectField field={fieldDef} value={value} onChange={onChange} error={error} depth={depth} />;

    default:
      return <TextField field={fieldDef} value={value} onChange={onChange} error={error} />;
  }
};

// Dynamic Form Component
export const DynamicForm = ({ schema, initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with default values or existing data
  useEffect(() => {
    const initData = { ...initialData };
    
    // Set defaults for fields that don't have values
    Object.entries(schema.fields).forEach(([fieldName, fieldDef]) => {
      if (initData[fieldName] === undefined && fieldDef.default !== undefined) {
        if (fieldDef.default === 'Date.now') {
          initData[fieldName] = new Date();
        } else {
          initData[fieldName] = fieldDef.default;
        }
      }
    });

    setFormData(initData);
  }, [initialData, schema]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    Object.entries(schema.fields).forEach(([fieldName, fieldDef]) => {
      const value = formData[fieldName];

      // Required field validation
      if (fieldDef.required && (value === undefined || value === null || value === '')) {
        newErrors[fieldName] = `${fieldDef.label} is required`;
      }

      // Number validation
      if (fieldDef.type === 'Number' && value !== undefined && value !== null) {
        if (fieldDef.min !== undefined && value < fieldDef.min) {
          newErrors[fieldName] = `${fieldDef.label} must be at least ${fieldDef.min}`;
        }
        if (fieldDef.max !== undefined && value > fieldDef.max) {
          newErrors[fieldName] = `${fieldDef.label} must be at most ${fieldDef.max}`;
        }
      }

      // String validation
      if (fieldDef.type === 'String' && value) {
        if (fieldDef.validation?.minLength && value.length < fieldDef.validation.minLength) {
          newErrors[fieldName] = `${fieldDef.label} must be at least ${fieldDef.validation.minLength} characters`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: error.message || 'An error occurred while submitting the form' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="dynamic-form">
      {Object.entries(schema.fields).map(([fieldName, fieldDef]) => (
        <div key={fieldName} className="form-group">
          {renderField(
            fieldName,
            fieldDef,
            formData[fieldName],
            (value) => handleFieldChange(fieldName, value),
            formData
          )}
        </div>
      ))}

      {errors.submit && (
        <div className="error-message submit-error">{errors.submit}</div>
      )}

      <div className="form-actions">
        <button type="submit" disabled={isSubmitting} className="btn btn-primary">
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default DynamicForm;
